<template>
  <Modal
    :model-value="modelValue"
    :show-close="false"
    :content-class="'max-w-6xl'"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <template #title>
      <div class="flex items-start justify-between gap-4 pr-24">
        <span>{{ title || t('documentPreview') }}</span>
      </div>
    </template>

    <div class="relative space-y-4">
      <div class="absolute right-0 top-[-3.5rem] flex items-center gap-1">
        <CVButton
          size="icon"
          variant="ghost"
          :disabled="isLoading || !src || !!errorMessage || !canPrintInline"
          :title="t('printDocument')"
          @click="printPdf"
        >
          <PrinterIcon class="size-4" />
        </CVButton>
        <CVButton
          size="icon"
          variant="ghost"
          :disabled="isLoading || !src || !!errorMessage"
          :title="t('downloadDocument')"
          @click="emit('download')"
        >
          <DownloadIcon class="size-4" />
        </CVButton>
      </div>

      <div
        class="flex h-[70vh] items-center justify-center overflow-hidden rounded-md border border-obsidian-200 bg-obsidian-50"
      >
        <div v-if="isLoading" class="flex flex-col items-center gap-3 text-obsidian-600">
          <Spinner class="size-8" />
          <p>{{ t('loadingDocument') }}</p>
        </div>
        <div
          v-else-if="errorMessage"
          class="mx-auto max-w-md space-y-2 px-6 text-center text-obsidian-600"
        >
          <p class="font-medium text-obsidian-800">{{ t('loadingErrorTitle') }}</p>
          <p>{{ errorMessage }}</p>
        </div>
        <img
          v-else-if="isImage && !imageFailed"
          :src="src"
          class="max-h-[70vh] max-w-full bg-white object-contain"
          :alt="title || t('documentPreview')"
          @error="imageFailed = true"
        />
        <iframe
          v-else-if="isPdf"
          ref="iframeRef"
          :src="src"
          class="h-[70vh] w-full bg-white"
          :title="title || t('documentPreview')"
        />
        <div v-else-if="src" class="mx-auto max-w-md space-y-4 px-6 text-center text-obsidian-600">
          <p class="font-medium text-obsidian-800">{{ t('previewNotSupportedTitle') }}</p>
          <p>{{ t('previewNotSupportedBody') }}</p>
          <CVButton variant="default" @click="emit('download')">
            {{ t('downloadDocument') }}
          </CVButton>
        </div>
      </div>

      <div class="flex justify-end gap-3">
        <CVButton variant="secondary" @click="emit('update:modelValue', false)">
          {{ t('close') }}
        </CVButton>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Spinner from '@/components/icons/Spinner.vue';
import { DownloadIcon, PrinterIcon } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  modelValue: boolean;
  src: string;
  mimeType?: string;
  title?: string;
  isLoading?: boolean;
  errorMessage?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'download'): void;
}>();

const { t } = useI18n();
const iframeRef = ref<HTMLIFrameElement | null>(null);
const imageFailed = ref(false);
const isImage = computed(() => props.mimeType?.startsWith('image/') ?? false);
const isPdf = computed(() => props.mimeType === 'application/pdf');
const canPrintInline = computed(() => (isPdf.value || isImage.value) && !imageFailed.value);

watch(
  () => props.src,
  () => {
    imageFailed.value = false;
  },
);

const printViaHiddenIframe = (html: string) => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';
  iframe.style.opacity = '0';
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument ?? iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    return;
  }

  doc.open();
  doc.write(html);
  doc.close();

  iframe.onload = () => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  };
};

const printPdf = () => {
  if (!canPrintInline.value) return;

  if (isImage.value) {
    printViaHiddenIframe(
      `<html><body style="margin:0;display:flex;justify-content:center;align-items:center"><img src="${props.src}" style="max-width:100%;max-height:100vh" onload="window.print()"/></body></html>`,
    );
    return;
  }

  const iframeWindow = iframeRef.value?.contentWindow;

  if (iframeWindow) {
    iframeWindow.focus();
    iframeWindow.print();
    return;
  }

  printViaHiddenIframe(
    `<html><body style="margin:0"><iframe src="${props.src}" style="width:100%;height:100vh;border:none" onload="window.print()"></iframe></body></html>`,
  );
};
</script>

<i18n lang="json">
{
  "en": {
    "documentPreview": "Document Preview",
    "loadingDocument": "Loading document...",
    "loadingErrorTitle": "The document could not be loaded.",
    "close": "Close",
    "printDocument": "Print",
    "downloadDocument": "Download document",
    "previewNotSupportedTitle": "No inline preview for this file type",
    "previewNotSupportedBody": "The browser cannot display this file. Download it and open it on your device."
  },
  "de": {
    "documentPreview": "Dokumentenvorschau",
    "loadingDocument": "Dokument wird geladen...",
    "loadingErrorTitle": "Das Dokument konnte nicht geladen werden.",
    "close": "Schließen",
    "printDocument": "Drucken",
    "downloadDocument": "Dokument herunterladen",
    "previewNotSupportedTitle": "Keine Vorschau für diesen Dateityp",
    "previewNotSupportedBody": "Der Browser kann diese Datei nicht anzeigen. Bitte herunterladen und lokal öffnen."
  },
  "es": {
    "documentPreview": "Vista previa del documento",
    "loadingDocument": "Cargando documento…",
    "loadingErrorTitle": "No se pudo cargar el PDF.",
    "close": "Cerrar",
    "printDocument": "Imprimir",
    "downloadDocument": "Descargar documento",
    "previewNotSupportedTitle": "No se puede mostrar la vista previa de este tipo de archivo.",
    "previewNotSupportedBody": "El navegador no puede mostrar este archivo. Descárgalo y ábrelo en tu dispositivo."
  }
}
</i18n>
